<template>
  <div class="highlights-content__unit">
    <div class="highlights-content__blockquote-box">
      <!-- the prettier ignore are needed to prevent line breaks in blockquotes,
           where we have white-space: pre, so extra line breaks would go to the
           resulting HTML output -->
      <!-- prettier-ignore -->
      <div class="highlights-content__icons-box">
        <div v-if="highlight.highlight_type === 'bookmark'">
          <span class="iconfont iflag2  highlights-content__bookmark-icon"></span>
        </div>
        <img
          v-else
          class="highlights-content__blockquote-image"
          :src="require('@/assets.app/images/blockquote.svg')"
        />
        <div class="highlights-content__icons-right">
          <IconButton
            icon="ikindle-copy"
            :tooltip="copyTooltip"
            @click="copyHighlight(highlight)"
          />
        </div>
      </div>
      <blockquote class="highlights-content__blockquote">
        {{ highlight.quote }}
      </blockquote>
      <!--div>
        {{ highlight.contentTitle }}
      </div-->
      <div class="highlights-content__link-date">
        <router-link
          class="highlights-content__link"
          :to="highlight.contentLink"
          >Go to
          {{
            highlight.highlight_type === 'bookmark' ? 'bookmark' : 'highlight'
          }}
          <span class="highlights-content__link-icon iconfont ishare-1"></span>
        </router-link>
        <p class="highlights-content__date">
          {{
            highlight.highlight_type === 'bookmark'
              ? 'Bookmarked on'
              : 'Highlighted on'
          }}:
          <span class="highlights-content__date-num">{{ getDate() }}</span>
        </p>
        <button
          class="highlights-content__delete-btn"
          @click="showDeletePopup(highlight)"
        >
          Delete
        </button>
        <DeleteHighlightPopup
          ref="deletePopup"
          @confirmDelete="deleteHighlight"
        />
      </div>
    </div>

    <div
      v-if="
        highlight.id !== highlightEditId &&
        highlight.highlight_type !== 'bookmark'
      "
      class="highlights-content__notes"
    >
      <h3 class="highlights-content__title">Your Notes</h3>
      <p
        class="highlights-content__text add-finished"
        @click="toggleEdit(highlight)"
      >
        <span v-if="highlight.text">{{ highlight.text }}</span>
        <em v-else> Add note </em>
      </p>
    </div>

    <div
      v-show="
        highlightEditId === highlight.id &&
        highlight.highlight_type !== 'bookmark'
      "
      class="highlights-content__notes highlights-content__notes--edit"
    >
      <h3 class="highlights-content__title">Your Notes</h3>
      <form>
        <InputWithContextMenu
          ref="input"
          v-model="highlight.text"
          elementType="textarea"
          type="text"
          class="highlights-content__text"
          @blur="saveEdit(highlight)"
        />
      </form>
      <div class="highlights-content__btn-box">
        <button
          v-if="highlightEditId && highlight.id === highlightEditId"
          class="highlights-content__btn highlights-content__btn--save btns btns--minimum"
          @click="saveEdit(highlight)"
        >
          Save
        </button>
        <button
          v-if="highlightEditId && highlight.id === highlightEditId"
          class="highlights-content__btn highlights-content__btn--cancel btns btns--minimum btns--white"
          @click="toggleEdit(highlight)"
        >
          Cancel
        </button>
      </div>
    </div>

    <!--div class="highlights-content__share">
      <div
        class="highlights-content__share-btn-box highlights-content__share-btn-box--hide-m"
      ></div>
      <div class="highlights-content__share-link">
              <p class="highlights-content__share-text">Share:</p>
              <a class="highlights-content__share-link-c" title="Facebook"><span class="iconfont ifacebook"></span></a>
              <a class="highlights-content__share-link-c"  title="Twitter"><span class="iconfont itwitter"></span></a>
              <a class="highlights-content__share-link-c"  title="Email"><span class="iconfont ilink"></span></a>
       </div>
    </div-->

    <!-- We don't have a share widget for now, commented out above,
    just display the public link, if the highlight is shared alredy from the annotator menu-->
    <div v-if="highlight.is_public" class="highlights-content__share">
      <div
        class="highlights-content__share-btn-box highlights-content__share-btn-box--hide-m"
      ></div>
      <div class="highlights-content__share-link">
        <router-link
          :to="highlight.publicLink"
          class="highlights-content__share-link-c"
        >
          <span class="iconfont ilink"></span>
        </router-link>
      </div>
    </div>

    <div v-if="showFrom" class="highlights-content__from">
      <p class="highlights-content__from-text">
        From:
        <router-link
          class="highlights-content__from-link"
          :to="highlight.contentLink"
        >
          {{ content.doc.title }}. By {{ content.doc.author }}
        </router-link>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue'

import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'

import { setFocus } from '@/helpers/form'
import { formatDate } from '@/helpers/time'
import { backend } from '@/services/backend'
import { ContentView, AnnotationView } from '@/models/doc'
import { DocView } from '@/models/doc'
import IconButton from '@/app/components/button/IconButton.vue'
import DeleteHighlightPopup from './DeleteHighlightPopup.vue'

const props = withDefaults(
  defineProps<{
    doc: DocView
    content: ContentView
    highlight: AnnotationView
    showFrom?: boolean
  }>(),
  {
    showFrom: false,
  },
)

const deletePopup = ref<InstanceType<typeof DeleteHighlightPopup> | null>(null)
const input = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const highlightEditId = ref('')
const copyTooltip = ref('Copy')

function copyHighlight(highlight: AnnotationView): void {
  props.doc.copyHighlight(highlight)
  copyTooltip.value = 'Copied!'
  setTimeout(() => {
    copyTooltip.value = 'Copy'
  }, 2000)
}

function showDeletePopup(highlight: AnnotationView): void {
  deletePopup.value?.show(highlight)
}

function deleteHighlight(highlight: AnnotationView): void {
  props.doc.deleteHighlight(highlight)
}

function toggleEdit(highlight: AnnotationView): void {
  if (highlightEditId.value) {
    highlightEditId.value = ''
  } else {
    highlightEditId.value = highlight.id
    nextTick(() => {
      // Focus the edtior field after switching to edit mode.
      setFocus(input.value?.$el as HTMLElement)
    })
  }
}

function saveEdit(highlight: AnnotationView): void {
  // Save highlight note changes.
  backend.updateHighlightNote(highlight)
  toggleEdit(highlight)
}

function getDate(): string {
  if (props.highlight.created) {
    return formatDate(props.highlight.created)
  }
  return ''
}
</script>
